import { createContext, useContext } from "react";
import { ImagePreviewContextValue, ImagePreviewHandlersValue } from "./image.types";

/**
 * Context for the image preview state (opened state, current images, current index).
 */
export const ImagePreviewStateContext = createContext<ImagePreviewContextValue | null>(null);

/**
 * Context for the image preview handlers (interaction and registration methods).
 */
export const ImagePreviewHandlersContext = createContext<ImagePreviewHandlersValue | null>(null);

/**
 * Hook to access the ImagePreview state context.
 *
 * @throws Error if used outside of an ImagePreview provider.
 * @returns The current ImagePreview context state.
 */
export const useImagePreviewState = () => {
    const context = useContext(ImagePreviewStateContext);
    if (!context) {
        throw new Error("useImagePreviewState must be used within an ImagePreview provider");
    }
    return context;
};

/**
 * Hook to access the ImagePreview handlers context.
 *
 * @throws Error if used outside of an ImagePreview provider.
 * @returns The ImagePreview interaction and registration handlers.
 */
export const useImagePreviewHandlers = () => {
    const context = useContext(ImagePreviewHandlersContext);
    if (!context) {
        throw new Error("useImagePreviewHandlers must be used within an ImagePreview provider");
    }
    return context;
};

/**
 * Hook to access both state and handlers from the ImagePreview context.
 *
 * @deprecated Use useImagePreviewState and useImagePreviewHandlers separately for better performance.
 * @returns An object combining state and handlers from the context.
 */
export const useImagePreviewContext = () => {
    const state = useImagePreviewState();
    const handlers = useImagePreviewHandlers();
    return { ...state, handlers };
};
